import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import api from '../api/axios';
import toast from 'react-hot-toast';

const PLACEHOLDER = '/assets/no-photo.svg';

export default function MyProfilePage() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/profiles/me')
      .then(({ data }) => setProfile(data.profile))
      .finally(() => setLoading(false));
  }, []);

  const deletePhoto = async (photoId) => {
    try {
      await api.delete(`/profiles/me/photos/${photoId}`);
      toast.success('Photo deleted');
      const { data } = await api.get('/profiles/me');
      setProfile(data.profile);
    } catch { toast.error('Delete failed'); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50"><Navbar />
        <div className="flex justify-center py-20">
          <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
        </div>
      </div>
    );
  }

  const completeness = profile?.profile_score || 0;

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <h1 className="font-heading text-2xl font-bold text-gray-900">My Profile</h1>
          <Link to="/profile/setup" className="btn-primary text-sm py-2 px-4">Edit Profile</Link>
        </div>

        {/* Completeness */}
        <div className="card p-5 mb-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-gray-700">Profile Completeness</p>
            <p className="font-bold text-primary">{completeness}%</p>
          </div>
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-primary to-gold rounded-full transition-all" style={{ width: `${completeness}%` }} />
          </div>
          {completeness < 80 && (
            <p className="text-xs text-gray-500 mt-2">Complete your profile to get more matches! 🎯</p>
          )}
        </div>

        {/* Profile info */}
        <div className="card p-6 mb-6">
          <div className="flex items-start gap-4 mb-6">
            <div className="relative">
              <img src={profile?.photos?.[0] || PLACEHOLDER} alt=""
                className="w-20 h-20 rounded-full object-cover border-4 border-primary-100" />
              {profile?.is_premium && (
                <span className="absolute -bottom-1 -right-1 bg-gold text-white text-xs px-1.5 py-0.5 rounded-full">⭐</span>
              )}
            </div>
            <div>
              <h2 className="font-heading text-xl font-bold text-gray-900">
                {profile?.first_name} {profile?.last_name}
              </h2>
              <p className="text-sm text-gray-500 font-mono">{profile?.profile_id}</p>
              <span className={`inline-block mt-1 text-xs px-2 py-0.5 rounded-full font-medium ${
                profile?.profile_status === 'active' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
              }`}>
                {profile?.profile_status === 'active' ? '✅ Active' : '⚠️ Incomplete'}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2 text-sm">
            {[
              ['Age', `${profile?.age} yrs`],
              ['District', profile?.district],
              ['Denomination', profile?.denomination?.replace(/_/g, ' ')],
              ['Education', profile?.education_level?.replace(/_/g, ' ')],
              ['Occupation', profile?.occupation_type?.replace(/_/g, ' ')],
              ['Marital Status', profile?.marital_status?.replace(/_/g, ' ')],
            ].map(([l, v]) => v ? (
              <div key={l} className="bg-gray-50 rounded-lg px-3 py-2">
                <p className="text-xs text-gray-400">{l}</p>
                <p className="font-medium text-gray-800 capitalize">{v}</p>
              </div>
            ) : null)}
          </div>
        </div>

        {/* Photos */}
        <div className="card p-5 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading font-semibold text-gray-900">Photos ({profile?.photos?.length || 0}/6)</h3>
          </div>
          <div className="grid grid-cols-3 gap-3">
            {(profile?.photos || []).map((photo, i) => (
              <div key={i} className="relative group">
                <img src={photo} alt="" className="w-full h-28 object-cover rounded-xl" />
                {i === 0 && <span className="absolute top-1 left-1 text-xs bg-primary text-white px-1.5 py-0.5 rounded">Primary</span>}
                <button onClick={() => deletePhoto(photo)}
                  className="absolute top-1 right-1 bg-red-500 text-white text-xs w-5 h-5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  ×
                </button>
              </div>
            ))}
            {(profile?.photos?.length || 0) < 6 && (
              <Link to="/profile/setup" className="h-28 border-2 border-dashed border-gray-300 rounded-xl flex items-center justify-center text-gray-400 hover:border-primary hover:text-primary transition-colors">
                <span className="text-2xl">+</span>
              </Link>
            )}
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          {[
            { label: 'Profile Views',   value: profile?.profile_views || 0, icon: '👁️' },
            { label: 'Interests Sent',  value: '—', icon: '💌' },
            { label: 'Shortlisted By',  value: '—', icon: '⭐' },
          ].map(({ label, value, icon }) => (
            <div key={label} className="card p-4 text-center">
              <p className="text-2xl mb-1">{icon}</p>
              <p className="font-bold text-xl text-primary">{value}</p>
              <p className="text-xs text-gray-500">{label}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
